import { useEffect } from 'react';
import { create } from 'zustand';
import { agentsApi, type Agent } from '../services/api';

/**
 * The agents acting in each project, as `GET /api/projects/{id}/agents`
 * returns them: what the sidebar, the agent index and every agent picker
 * list, and what an agent page names itself from. Loaded once per project
 * per session — first from the first load (`usersApi.start`), which carries
 * every project's (`prime`) — and `load(id, true)` refetches, and a write updates its row.
 */
interface AgentsState {
  byProject: Record<string, Agent[]>;
  load: (projectId: string, force?: boolean) => Promise<void>;
  /** Lists that arrived with the workspace, for the projects not yet loaded:
   *  a list already here may carry a write made since that response began. */
  prime: (byProject: Record<string, Agent[]>) => void;
  /** An agent created or changed here, in its project's list. */
  upsert: (agent: Agent) => void;
  /** An agent deleted here, out of its project's list. */
  drop: (agentId: string) => void;
}

/** By name — the order every list shows. */
const ordered = (agents: Agent[]) => [...agents].sort((a, b) => a.name.localeCompare(b.name));

/**
 * The agent a project opens on for this person: the first one they reach in
 * it, by when it was made. A project has no default agent
 * (docs/projects/index.md § The agent) — this is only where a screen that
 * names no agent lands, never what a write is addressed to. Null until the
 * project's list has answered, or when this person reaches no agent in it.
 */
export function homeAgentId(agents: Agent[] | undefined): string | null {
  if (!agents?.length) return null;
  const first = [...agents].sort(
    (a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? '') || a.id.localeCompare(b.id),
  )[0];
  return first.id;
}

const inflight = new Map<string, Promise<void>>();
// Writes made here, per project: a listing that started before one is stale
// when it lands — a new project's fetch would put back the name its first
// agent was just renamed from — so it is asked again instead.
const writes = new Map<string, number>();

export const useAgentsStore = create<AgentsState>((set, get) => ({
  byProject: {},
  load: (projectId, force = false) => {
    if (!force && get().byProject[projectId]) return Promise.resolve();
    const pending = inflight.get(projectId);
    if (pending) return pending;
    const seen = writes.get(projectId) ?? 0;
    const request = agentsApi
      .list(projectId)
      .then((agents) => {
        if ((writes.get(projectId) ?? 0) !== seen) {
          inflight.delete(projectId);
          return get().load(projectId, true);
        }
        set((state) => ({ byProject: { ...state.byProject, [projectId]: ordered(agents) } }));
      })
      .catch(() => {
        /* not loaded: a list that never answered judges nothing (usePinUpkeep) */
      })
      .finally(() => {
        // A stale listing hands over to a fresh one; only its own entry is cleared.
        if (inflight.get(projectId) === request) inflight.delete(projectId);
      });
    inflight.set(projectId, request);
    return request;
  },
  prime: (lists) =>
    set((state) => {
      const fresh = Object.entries(lists).filter(([projectId]) => !(projectId in state.byProject));
      if (fresh.length === 0) return state;
      return { byProject: { ...state.byProject, ...Object.fromEntries(fresh.map(([id, list]) => [id, ordered(list)])) } };
    }),
  upsert: (agent) => {
    writes.set(agent.project_id, (writes.get(agent.project_id) ?? 0) + 1);
    set((state) => {
      const list = state.byProject[agent.project_id] ?? [];
      return { byProject: { ...state.byProject, [agent.project_id]: ordered([...list.filter((a) => a.id !== agent.id), agent]) } };
    });
  },
  drop: (agentId) => {
    for (const [projectId, list] of Object.entries(get().byProject)) {
      if (list.some((a) => a.id === agentId)) writes.set(projectId, (writes.get(projectId) ?? 0) + 1);
    }
    set((state) => ({
      byProject: Object.fromEntries(
        Object.entries(state.byProject).map(([projectId, list]) => [projectId, list.filter((a) => a.id !== agentId)]),
      ),
    }));
  },
}));

const NONE: Agent[] = [];

/** Has the project's list answered? An empty list that has is a person who
 *  reaches no agent here; one that has not is a list still on its way. */
export function useProjectAgentsLoaded(projectId: string | undefined): boolean {
  return useAgentsStore((s) => !!projectId && projectId in s.byProject);
}

/** The agent `projectId` opens on for this person (`homeAgentId`), loading
 *  the project's agents on first use. */
export function useHomeAgentId(projectId: string | null | undefined): string | null {
  const agents = useAgentsStore((s) => (projectId ? s.byProject[projectId] : undefined));
  const load = useAgentsStore((s) => s.load);
  useEffect(() => {
    if (projectId) void load(projectId);
  }, [projectId, load]);
  return homeAgentId(agents);
}

/** The project's agents, loading them on first use. Empty until they land. */
export function useProjectAgents(projectId: string | undefined): Agent[] {
  const agents = useAgentsStore((s) => (projectId ? s.byProject[projectId] : undefined));
  const load = useAgentsStore((s) => s.load);
  useEffect(() => {
    if (projectId) void load(projectId);
  }, [projectId, load]);
  return agents ?? NONE;
}
